import { Inject, Injectable } from '@angular/core';
import { ColumnDataInterface, TableCustomizationService } from './_abstract-table-customization.service';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { CrmDependencies, CrmInjectionToken } from '../../tokens-and-interfaces';
import {
  ContactCustomColumns,
  CrmFieldService,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
} from '../crm-services/field.service';
import { TranslateService } from '@ngx-translate/core';
import { map, switchMap, take } from 'rxjs/operators';
import { PAGE_ROUTES } from '../../constants';
import { CrmObjectInterface, FieldSchemaInterface } from '@vendasta/crm';
import { PrimaryCompanyNameComponent } from '../../shared-components/custom-table-cells/primary-company-name.component';
import { format, ParsedNumber, parseNumber } from 'libphonenumber-js';
import { GalaxyColumnDef, CustomCell } from '@vendasta/galaxy/table';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { RestrictedGroupService } from '../restricted-group.service';

@Injectable()
export class ContactTableCustomizationService extends TableCustomizationService {
  private contactLinkBuild$$ = new BehaviorSubject('');

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    protected override readonly crmFieldService: CrmFieldService,
    private readonly translate: TranslateService,
    private readonly restrictedGroupService: RestrictedGroupService,
  ) {
    super(crmFieldService);

    this.config.viewProfileFlag$
      .pipe(
        take(1),
        switchMap((featureFlag) => {
          if (featureFlag) {
            return this.config.routePrefix$;
          } else {
            return of('');
          }
        }),
      )
      .subscribe((routePrefix) => {
        if (!routePrefix) {
          return;
        }
        const contactProfileViewRoute = `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
        if (routePrefix) {
          this.contactLinkBuild$$.next(`${routePrefix}/${contactProfileViewRoute}`);
        }
      });
  }

  private hideIDColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToHide = [
      this.crmFieldService.getFieldId(StandardExternalIds.FirstName),
      this.crmFieldService.getFieldId(StandardExternalIds.LastName),
      SystemFieldIds.ContactExternalID,
      SystemFieldIds.ContactID,
      StandardIds.ContactPrimaryCompanyID,
      ContactCustomColumns.FullAddress,
    ];
    for (const identifier of columnIdentifiersToHide) {
      const index = columns.findIndex((c) => c.id === identifier);
      if (index >= 0) {
        columns[index].hidden = true;
      }
    }
    return columns;
  }

  private addPrimaryCompanyNameColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.PrimaryCompanyName,
        title: this.translate.instant('CONTACT.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.PRIMARY_COMPANY_NAME'),
        hidden: true,
      },
      ...columns,
    ];
  }

  private async addPrimaryCompanyNameRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): Promise<void> {
    let contactPrimaryCompanyID = '';
    const contactPrimaryCompanyIDFieldValue = this.crmFieldService.getFieldValueFromCrmObject(
      crmObject,
      StandardIds.ContactPrimaryCompanyID,
    );
    if (contactPrimaryCompanyIDFieldValue?.stringValue) {
      contactPrimaryCompanyID = contactPrimaryCompanyIDFieldValue.stringValue;
    }

    columnData[ContactCustomColumns.PrimaryCompanyName] = {
      cellType: 'custom',
      value: contactPrimaryCompanyID,
      component: PrimaryCompanyNameComponent,
    } as CustomCell;
  }

  private addFullNameColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.FullName,
        title: this.translate.instant('CONTACT.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.FULL_NAME'),
        sticky: true,
        sortable: false,
      },
      ...columns,
    ];
  }

  private formatPhoneNumber(phoneNumber: string): string {
    const parsedNumber = parseNumber(phoneNumber, {
      defaultCountry: 'US',
    });
    if (Object.keys(parsedNumber).length === 0) {
      return '';
    }

    return format(parsedNumber as ParsedNumber, 'INTERNATIONAL');
  }

  private addFullNameRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    columnData[ContactCustomColumns.FullName] = {
      cellType: 'text',
      value: this.getRowFullName(crmObject),
      link: this.contactLinkBuild$$.value.replace(':crmObjectId', crmObject.crmObjectId || ''),
    };
  }

  getRowFullName(crmObject: CrmObjectInterface): string {
    const firstName = this.crmFieldService.getFieldId(StandardExternalIds.FirstName);
    const lastName = this.crmFieldService.getFieldId(StandardExternalIds.LastName);
    const email = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const phone = this.crmFieldService.getFieldId(StandardExternalIds.PhoneNumber);

    const firstNameData = crmObject.fields?.find((f) => f.fieldId === firstName);
    const lastNameData = crmObject.fields?.find((f) => f.fieldId === lastName);
    const emailData = crmObject.fields?.find((f) => f.fieldId === email);
    const phoneData = crmObject.fields?.find((f) => f.fieldId === phone);

    if (firstNameData?.stringValue || lastNameData?.stringValue) {
      return `${firstNameData?.stringValue ?? ''} ${lastNameData?.stringValue ?? ''}`.trim();
    } else {
      if (emailData) {
        return emailData.stringValue || '';
      } else {
        return this.formatPhoneNumber(phoneData?.stringValue || '') || '';
      }
    }
  }

  getAssociationInfo(crmObject: CrmObjectInterface): string {
    const email = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const emailData = crmObject.fields?.find((f) => f.fieldId === email);
    return emailData?.stringValue || '';
  }

  private addFullAddressColumnData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    columnData[ContactCustomColumns.FullAddress] = {
      cellType: 'text',
      value: this.getRowFullAddress(crmObject),
    };
  }

  customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    this.customizeNextTaskDueDateCellData(columnData);
    this.addFullNameRowData(crmObject, schemaMap, columnData);
    this.addPrimaryCompanyNameRowData(crmObject, schemaMap, columnData);
    this.addFullAddressColumnData(crmObject, schemaMap, columnData);
    this.addCustomCells(this.config.contact?.tableCustomCells || [], crmObject, columnData);
  }

  private customizeNextTaskDueDateCellData(columnData: ColumnDataInterface): void {
    const cell = columnData[this.crmFieldService.getFieldId(StandardIds.ContactNextTaskDueDate)];
    if (cell) {
      cell.cellType = 'timestamp';
    }
  }

  customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[] {
    let customizedColumns = this.hideIDColumns(columns);
    customizedColumns = this.addPrimaryCompanyNameColumn(customizedColumns);
    customizedColumns = this.addFullNameColumn(customizedColumns);
    customizedColumns = this.addFullAddressColumn(customizedColumns);

    const customCells = this.config.contact?.tableCustomCells || [];
    customizedColumns = this.addCustomColumns(
      customCells.map((c) => c.columnDefinition),
      customizedColumns,
    );
    if (isMobile) {
      customizedColumns = this.customizeColumnsMobile(customizedColumns);
    }
    return [...customizedColumns];
  }

  customizeColumnsMobile(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToShow = new Map<string, boolean>([
      [this.crmFieldService.getFieldId(StandardExternalIds.Email), true],
      [ContactCustomColumns.FullName, true],
    ]);

    columns.forEach((column) => {
      column.hidden = !columnIdentifiersToShow.get(column.id);
    });
    return [...columns];
  }

  getRowFullAddress(crmObject: CrmObjectInterface): string {
    const addressLineOne = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressLine1);
    const addressLineTwo = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressLine2);
    const city = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressCity);
    const state = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressState);
    const postalCode = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressPostalCode);
    const country = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressCountry);

    const addressLineOneData = crmObject.fields?.find((f) => f.fieldId === addressLineOne);
    const addressLineTwoData = crmObject.fields?.find((f) => f.fieldId === addressLineTwo);
    const cityData = crmObject.fields?.find((f) => f.fieldId === city);
    const stateData = crmObject.fields?.find((f) => f.fieldId === state);
    const postalCodeData = crmObject.fields?.find((f) => f.fieldId === postalCode);
    const countryData = crmObject.fields?.find((f) => f.fieldId === country);

    const fullAddress = [
      addressLineOneData?.stringValue,
      addressLineTwoData?.stringValue,
      cityData?.stringValue,
      stateData?.stringValue,
      countryData?.stringValue,
      postalCodeData?.stringValue,
    ];

    return fullAddress.filter((value) => value !== undefined).join(', ');
  }

  private addFullAddressColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.FullAddress,
        title: this.translate.instant('LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.FULL_ADDRESS'),
        hidden: true,
        sortable: false,
      },
      ...columns,
    ];
  }

  private makeGroupFilterRestricted(
    restrictedToGroupId: string,
    filters: GalaxyFilterDefinitionInterface[],
  ): GalaxyFilterDefinitionInterface[] {
    const emailFieldIdIndex = filters.findIndex(
      (f) => f.fieldId === this.crmFieldService.getFieldId(StandardExternalIds.Email),
    );
    if (emailFieldIdIndex > -1) {
      filters[emailFieldIdIndex].preset = true;
    }
    if (restrictedToGroupId) {
      const groupIDIndex = filters.findIndex((f) => f.fieldId === SystemFieldIds.ContactGroupID);
      if (groupIDIndex > -1) {
        filters[groupIDIndex].preset = true;
        filters[groupIDIndex].lockedValue = true;
      }
    }
    return filters;
  }

  customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]> {
    const overrides$ = this.config.contact?.filterInputOverrides$ ?? of([]);
    const appliedOverrides$ = overrides$.pipe(map((overrides) => this.overrideCustomFilters(filters, overrides)));
    return combineLatest([this.restrictedGroupService.getCurrentGroupId(), appliedOverrides$]).pipe(
      map(([restrictedMarketId, filters]: [string, GalaxyFilterDefinitionInterface[]]) =>
        this.makeGroupFilterRestricted(restrictedMarketId, filters),
      ),
    );
  }

  async getInitialFilters(): Promise<GalaxyFilterInterface[]> {
    return firstValueFrom(this.restrictedGroupService.getCurrentGroupId()).then((selectedGroupId) => {
      const emailFieldId = this.crmFieldService.getFieldId(StandardExternalIds.Email);
      const filters: GalaxyFilterInterface[] = [
        {
          fieldId: emailFieldId,
          filterId: 'filterContactByEmail',
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
          values: [],
        },
      ];
      if (selectedGroupId) {
        filters.push({
          fieldId: SystemFieldIds.ContactGroupID,
          filterId: 'filterContactByRestrictedGroupID',
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
          values: [
            {
              string: selectedGroupId,
            },
          ],
        });
      }
      return filters;
    });
  }
}
